import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { apiClient, API_ENDPOINTS } from '../config/api';

interface ContactFaqDocument {
  id: number;
  displayName: string;
  originalFilename: string;
  isActive: boolean;
  uploadedAt: string;
  ownerUsername?: string | null;
  itemCount?: number;
}

interface ContactFaqManagerProps {
  onBackToDeveloper: () => void;
}

const extractErrorMessage = (err: unknown, fallback: string) => {
  if (axios.isAxiosError(err)) {
    return err.response?.data?.error?.message || fallback;
  }
  return err instanceof Error ? err.message : fallback;
};

const ContactFaqManager: React.FC<ContactFaqManagerProps> = ({ onBackToDeveloper }) => {
  const [faqDocuments, setFaqDocuments] = useState<ContactFaqDocument[]>([]);
  const [displayName, setDisplayName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadFaqDocuments = useCallback(async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.adminContactFaqDocuments);
      setFaqDocuments(response.data.faqDocuments || []);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'FAQ見本一覧の取得に失敗しました。'));
    }
  }, []);

  useEffect(() => {
    loadFaqDocuments();
  }, [loadFaqDocuments]);

  const handleUpload = async (file: File) => {
    setIsUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('displayName', displayName.trim() || file.name);
      await apiClient.post(API_ENDPOINTS.adminContactFaqDocuments, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDisplayName('');
      setSuccessMessage('FAQ見本をアップロードしました。');
      await loadFaqDocuments();
    } catch (err) {
      setError(extractErrorMessage(err, 'FAQ見本のアップロードに失敗しました。'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
    event.target.value = '';
  };

  const handleActivate = async (id: number) => {
    try {
      await apiClient.patch(API_ENDPOINTS.adminContactFaqDocument(id), { isActive: true });
      setSuccessMessage('有効なFAQ見本を切り替えました。');
      await loadFaqDocuments();
    } catch (err) {
      setError(extractErrorMessage(err, 'FAQ見本の有効化に失敗しました。'));
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('このFAQ見本を削除しますか？')) {
      return;
    }
    try {
      await apiClient.delete(API_ENDPOINTS.adminContactFaqDocument(id));
      setSuccessMessage('FAQ見本を削除しました。');
      await loadFaqDocuments();
    } catch (err) {
      setError(extractErrorMessage(err, 'FAQ見本の削除に失敗しました。'));
    }
  };

  const handleDownload = async (id: number, originalFilename: string) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.adminContactFaqDocumentDownload(id), {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = originalFilename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'FAQ見本のダウンロードに失敗しました。'));
    }
  };

  return (
    <div className="login-activity-page">
      <section className="login-activity-hero">
        <div>
          <span className="login-activity-eyebrow">Admin Visibility</span>
          <h2>FAQ見本管理</h2>
          <p>お問い合わせAIが参照するFAQ見本を管理します。</p>
        </div>
        <div className="login-activity-hero-actions">
          <button className="login-activity-back-button" onClick={onBackToDeveloper}>
            管理者画面へ戻る
          </button>
        </div>
      </section>

      {error && <div className="error-message"><span>{error}</span></div>}
      {successMessage && <div className="success-message"><span>{successMessage}</span></div>}

      <section className="login-activity-panel">
        <div className="login-activity-panel-header">
          <div>
            <h3>新しいFAQ見本をアップロード</h3>
            <p>Excel / CSV 形式で登録してください。</p>
          </div>
        </div>
        <div style={{ display: 'grid', gap: '0.75rem', maxWidth: 560 }}>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="表示名（省略時はファイル名）"
            style={{ padding: '0.275rem 0.425rem' }}
          />
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileChange}
            disabled={isUploading}
          />
        </div>
      </section>

      <section className="login-activity-panel">
        <div className="login-activity-panel-header">
          <div>
            <h3>登録済みFAQ見本</h3>
            <p>有効化されたFAQ見本がAI応答に使用されます。</p>
          </div>
        </div>
        <div className="login-activity-table-wrap">
          <table className="login-activity-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>表示名</th>
                <th>元ファイル名</th>
                <th>件数</th>
                <th>状態</th>
                <th>登録者</th>
                <th>登録日時</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {faqDocuments.length === 0 ? (
                <tr>
                  <td colSpan={8} className="login-activity-empty-cell">FAQ見本はまだ登録されていません。</td>
                </tr>
              ) : (
                faqDocuments.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>{item.displayName}</td>
                    <td>{item.originalFilename}</td>
                    <td>{item.itemCount || 0}</td>
                    <td>{item.isActive ? '有効' : '無効'}</td>
                    <td>{item.ownerUsername || '-'}</td>
                    <td>{new Date(item.uploadedAt).toLocaleString('ja-JP')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {!item.isActive && <button onClick={() => handleActivate(item.id)}>有効化</button>}
                        <button onClick={() => handleDownload(item.id, item.originalFilename)} style={{ padding: '0.275rem 0.425rem' }}>
                          ダウンロード
                        </button>
                        <button onClick={() => handleDelete(item.id)} style={{ padding: '0.275rem 0.425rem' }}>
                          削除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default ContactFaqManager;
